import React, { useState } from "react";
import { Field, FormSection, formValueSelector } from "redux-form";
import { connect } from "react-redux";
import { IconButton } from "@mui/material";
import { components } from "react-select";

import { CustomCheckbox } from "../../../../../../common/customCheckbox";
import { CustomSingleSelect } from "../../../../../../common/new-core-ui/CustomSingleSelect";
import FormSelectStyle from "../../../../../v2FormSelectStyle";
import styles from "../../../../JobStyle.module.css";
import DynamicMethodItemTypeAddEditModal from "./DynamicMethodItemTypeAddEditModal.jsx";

const FORM_NAME = "job_swms_form_v3";

const EnabledComponent = ({ sectionKey, selected, change, allData, loading, onOpenEdit }) => {
  const dataPath = `sections.${sectionKey}.data`;

  const onRemove = (item) => {
    change(`${dataPath}.enabled.item_${item.id}`, false);
    change(`${dataPath}.selected`, selected.filter((x) => String(x.id) !== String(item.id)));
    change(`${dataPath}.method_items`, allData.filter((x) => String(x.id) !== String(item.id)));
    change(`${dataPath}.enabled.item_${item.id}`, undefined);
  };

  return (
    <div className="container-fluid mt-2">
      <div className="row">
        {selected.length === 0 ? (
          <div className="col-12 text-center py-2" style={{ color: "red" }}>No items selected</div>
        ) : null}
        {selected.map((item, index) => {
          return (
            <div className="col-4 d-flex align-items-start py-1" key={String(item.id || item.value || index)}>
              <div style={{ flex: "1 1 auto" }}>
                <Field
                  component={CustomCheckbox}
                  name={`item_${item.id}`}
                  disabled={loading}
                  label={item.label || item.method_item || ""}
                  className="cs-select-v2"
                />
              </div>
              {item && item.isNew ? (
                <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                  <IconButton
                    disabled={loading}
                    style={{ height: "auto", padding: "0px" }}
                    onClick={() => onOpenEdit(item)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" className={styles.EditIcon} style={{ color: "#90bf00", height: "12px" }}>
                      <path fill="currentColor" d="M362.7 19.3L314.3 67.7 444.3 197.7l48.4-48.4c25-25 25-65.5 0-90.5L453.3 19.3c-25-25-65.5-25-90.5 0zm-71 71L58.6 323.5c-10.4 10.4-18 23.3-22.2 37.4L1 481.2C-1.5 489.7 .8 498.8 7 505s15.3 8.5 23.7 6.1l120.3-35.4c14.1-4.2 27-11.8 37.4-22.2L421.7 220.3 291.7 90.3z" />
                    </svg>
                  </IconButton>
                  <IconButton
                    disabled={loading}
                    style={{ height: "auto", padding: "0px" }}
                    onClick={() => onRemove(item)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" style={{ height: "12px", color: "red" }}>
                      <path fill="currentColor" d="M135.2 17.7L128 32H32C14.3 32 0 46.3 0 64S14.3 96 32 96H416c17.7 0 32-14.3 32-32s-14.3-32-32-32H320l-7.2-14.3C307.4 6.8 296.3 0 284.2 0H163.8c-12.1 0-23.2 6.8-28.6 17.7zM416 128H32L53.2 467c1.6 25.3 22.6 45 47.9 45H346.9c25.3 0 46.3-19.7 47.9-45L416 128z" />
                    </svg>
                  </IconButton>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DynamicMethodItemTypeSection = ({ sectionKey, allData = [], selected = [], enabled = {}, change, loading }) => {
  const [showModal, setShowModal] = useState(false);
  const [editItemId, setEditItemId] = useState(null);
  const dataPath = `sections.${sectionKey}.data`;

  const onOpenAdd = () => {
    setEditItemId(null);
    setShowModal(true);
  };

  const onOpenEdit = (item) => {
    if (!item || !item.isNew) {
      return;
    }
    setEditItemId(item.id);
    setShowModal(true);
  };

  const onCloseModal = (isSaved, payload) => {
    if (!isSaved || !payload) {
      setShowModal(false);
      setEditItemId(null);
      return;
    }

    if (editItemId) {
      const updated = allData.map((item) => {
        if (String(item.id) !== String(editItemId)) {
          return item;
        }

        return {
          ...item,
          method_item: payload.method_item,
          linked_documents: payload.linked_documents || null
        };
      });

      change(`${dataPath}.method_items`, updated);
      change(`${dataPath}.selected`, selected.map((item) => {
        if (String(item.id) !== String(editItemId)) {
          return item;
        }

        return {
          ...item,
          method_item: payload.method_item,
          linked_documents: payload.linked_documents || null,
          selected: true
        };
      }));
      setShowModal(false);
      setEditItemId(null);
      return;
    }

    const normalizedName = `${payload.method_item || ""}`.trim().toLowerCase();
    if (allData.findIndex((item) => `${item.method_item || ""}`.trim().toLowerCase() === normalizedName) !== -1) {
      setShowModal(false);
      setEditItemId(null);
      return;
    }

    const customId = `custom_${Date.now()}`;
    const newItem = {
      id: customId,
      method_item: payload.method_item,
      linked_documents: payload.linked_documents || null,
      selected: true,
      isNew: true
    };

    change(`${dataPath}.method_items`, [...allData, newItem]);
    change(`${dataPath}.enabled`, {
      ...enabled,
      [`item_${customId}`]: true
    });
    change(`${dataPath}.selected`, [...selected, { ...newItem, label: newItem.method_item, value: customId }]);

    setShowModal(false);
    setEditItemId(null);
  };

  const editItem = allData.find((item) => String(item.id) === String(editItemId));

  return (
    <div className="mb-3">
      <FormSection
        name="enabled"
        component={EnabledComponent}
        sectionKey={sectionKey}
        selected={selected}
        allData={allData}
        change={change}
        onOpenEdit={onOpenEdit}
        loading={loading}
      />

      {showModal ? (
        <DynamicMethodItemTypeAddEditModal
          visible={showModal}
          mode={editItemId ? "Edit" : "Add"}
          initialValues={editItem ? {
            method_item: editItem.method_item,
            linked_documents: editItem.linked_documents || null
          } : {
            method_item: "",
            linked_documents: null
          }}
          onClose={onCloseModal}
        />
      ) : null}

      <div className="container-fluid mt-2">
        <div className="row">
          <div className="col-4">
            <div className={styles.Label}>Select Item</div>
            <Field
              name="selected"
              component={CustomSingleSelect}
              options={allData.map((item) => ({ label: item.method_item, value: item.id, ...item }))}
              styles={FormSelectStyle}
              otherProps={{
                isMulti: true,
                isDisabled: loading,
                closeMenuOnSelect: false,
                hideSelectedOptions: false,
                menuPortalTarget: document.body,
                components: { dropdownIndicator: <components.DownChevron /> }
              }}
            />
          </div>
          <div className="col-4 d-flex align-items-end" style={{ gap: "5px" }}>
            <button type="button" disabled={loading} className={styles.PrimaryButton} onClick={onOpenAdd}>
              Add Item
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default connect((state, ownProps) => {
  const selector = formValueSelector(FORM_NAME);
  const dataPath = `sections.${ownProps.sectionKey}.data`;
  return {
    allData: selector(state, `${dataPath}.method_items`) || [],
    selected: selector(state, `${dataPath}.selected`) || [],
    enabled: selector(state, `${dataPath}.enabled`) || {}
  };
})(DynamicMethodItemTypeSection);
